/**
 * Business Categories and Subcategories Configuration
 * 
 * Centralized configuration for all business category/subcategory options
 * Used across: Onboarding, Business Dashboard, Admin, Deals Page
 */

export const BUSINESS_CATEGORIES = {
  'Food': [
    'Restaurant',
    'Pizza Shop',
    'Italian',
    'Sushi',
    'Mediterranean',
    'Mexican',
    'Bakery',
    'Coffee Shop',
    'Deli',
    'Dessert',
    'Food Truck',
    'Caterer',
    'Brewery',
    'Winery'
  ],
  'Professional Services': [
    'Accountant',
    'Attorney',
    'Financial Advisor',
    'Insurance Agent',
    'Realtor',
    'Mortgage Broker',
    'Tax Preparer',
    'Notary',
    'Marketing Agency',
    'Graphic Designer',
    'Business Consultant'
  ],
  'Local Services': [
    'HVAC',
    'Plumbing',
    'Electrical',
    'Roofing',
    'General Contractor',
    'Handyman',
    'Locksmith',
    'Appliance Repair',
    'House Cleaning',
    'Carpet Cleaning',
    'Pressure Washing',
    'Pest Control',
    'Junk Removal',
    'Moving Company',
    'Chimney Sweep',
    'Garage Door',
    'Pool Service',
    'Windows',
    'Gutters',
    'Shipping',
    'Childcare'
  ],
  'Retail': [
    'Clothing',
    'Jeweler',
    'Florist',
    'Pet Store',
    'Gift Shop',
    'Sporting Goods',
    'Bike Shop',
    'Furniture',
    'Antiques',
    'Thrift Store',
    'Art Gallery',
    'Books',
    'Toy Store',
    'Music Store',
    'Bridal',
    'Smoke Shop',
    'Liquor Store',
    'Hardware Store',
    'Electronics',
    'Grocery Store'
  ],
  'Wellness': [
    'Gym',
    'Yoga Studio',
    'Personal Trainer',
    'Pilates',
    'CrossFit',
    'Martial Arts',
    'Chiropractor',
    'Physical Therapy',
    'Dentist',
    'Orthodontist',
    'Optometrist',
    'Hearing Center',
    'Pharmacy',
    'Urgent Care',
    'Therapist',
    'Nutritionist',
    'Acupuncture',
    'Veterinarian',
    'Med Spa'
  ],
  'Beauty': [
    'Hair Salon',
    'Barbershop',
    'Nail Salon',
    'Spa',
    'Med Spa',
    'Tanning',
    'Waxing Studio',
    'Lash Studio',
    'Makeup Artist',
    'Tattoo Shop'
  ],
  'Garden & Outdoor': [
    'Landscaping',
    'Lawn Care',
    'Tree Service',
    'Hardscaping',
    'Garden Center',
    'Irrigation',
    'Fence Company',
    'Deck Builder',
    'Outdoor Lighting',
    'Patio'
  ],
  'Fun & Entertainment': [
    'Bowling',
    'Gaming',
    'Escape Room',
    'Mini Golf',
    'Go Karts',
    'Laser Tag',
    'Axe Throwing',
    'Trampoline Park',
    'Movie Theater',
    'Event Venue',
    'DJ',
    'Photographer',
    'Videographer',
    'Party Rentals',
    'Photo Booth',
    'Tours',
    'Boat Rental',
    'Fishing Charter',
    'Golf Course',
    'Sports League'
  ],
  'Tech': [
    'Computer Repair',
    'Phone Repair',
    'IT Services',
    'Web Design',
    'Smart Home',
    'Security Systems',
    'Networking',
    'Data Recovery'
  ],
  'Auto': [
    'Auto Repair',
    'Auto Body',
    'Detailing',
    'Tire Shop',
    'Oil Change',
    'Car Wash',
    'Dealership',
    'Towing',
    'Auto Glass',
    'Transmission'
  ]
};

// Get list of category names
export const CATEGORY_LIST = Object.keys(BUSINESS_CATEGORIES);

// Get subcategories for a given category
export const getSubcategories = (category) => {
  return BUSINESS_CATEGORIES[category] || [];
};

// Get all subcategories as a flat array
export const getAllSubcategories = () => {
  return Object.values(BUSINESS_CATEGORIES).flat();
};

// Find category by subcategory
export const getCategoryBySubcategory = (subcategory) => {
  for (const [category, subcategories] of Object.entries(BUSINESS_CATEGORIES)) {
    if (subcategories.includes(subcategory)) {
      return category;
    }
  }
  return null;
};

// Category colors for badges
export const CATEGORY_COLORS = {
  'Food': { bg: 'bg-orange-100', text: 'text-orange-700', border: 'border-orange-200' },
  'Professional Services': { bg: 'bg-blue-100', text: 'text-blue-700', border: 'border-blue-200' },
  'Local Services': { bg: 'bg-yellow-100', text: 'text-yellow-700', border: 'border-yellow-200' },
  'Retail': { bg: 'bg-pink-100', text: 'text-pink-700', border: 'border-pink-200' },
  'Wellness': { bg: 'bg-green-100', text: 'text-green-700', border: 'border-green-200' },
  'Beauty': { bg: 'bg-purple-100', text: 'text-purple-700', border: 'border-purple-200' },
  'Garden & Outdoor': { bg: 'bg-emerald-100', text: 'text-emerald-700', border: 'border-emerald-200' },
  'Fun & Entertainment': { bg: 'bg-red-100', text: 'text-red-700', border: 'border-red-200' },
  'Tech': { bg: 'bg-cyan-100', text: 'text-cyan-700', border: 'border-cyan-200' },
  'Auto': { bg: 'bg-slate-100', text: 'text-slate-700', border: 'border-slate-200' }
};

// Get color classes for a category
export const getCategoryColors = (category) => {
  return CATEGORY_COLORS[category] || { bg: 'bg-gray-100', text: 'text-gray-700', border: 'border-gray-200' };
};

export default BUSINESS_CATEGORIES;
